/**
 * ClientsManager.jsx — V4 Redesign
 * Client portfolio list view — matches VATICA dark design system
 */

import { useState, useEffect } from "react";
import {
  getMyClients,
  createMyClient,
  deleteMyClient,
} from "./api";
import ConfirmModal from "./ConfirmModal";

const CM_CSS = `
  /* ── ClientsManager ── */
  .cm-page { padding:24px; }

  .cm-hero { margin-bottom:28px; }
  .cm-hero-row { display:flex; align-items:flex-end; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:8px; }
  .cm-title { font-size:20px; font-weight:800; letter-spacing:-0.02em; color:var(--text); }
  .cm-sub { font-size:12px; color:var(--muted); margin-top:4px; }

  /* Stats strip */
  .cm-stats { display:flex; gap:12px; margin-bottom:24px; flex-wrap:wrap; }
  .cm-stat { background:var(--panel); border:1px solid var(--border); border-radius:14px; padding:14px 20px; display:flex; align-items:center; gap:12px; min-width:160px; }
  .cm-stat-icon { font-size:20px; width:38px; height:38px; display:flex; align-items:center; justify-content:center; border-radius:10px; background:rgba(59,158,255,0.08); border:1px solid rgba(59,158,255,0.12); flex-shrink:0; }
  .cm-stat-body { display:flex; flex-direction:column; }
  .cm-stat-num { font-family:'JetBrains Mono',monospace; font-size:20px; font-weight:700; color:var(--text); line-height:1; }
  .cm-stat-label { font-size:10px; color:var(--muted); text-transform:uppercase; letter-spacing:0.08em; margin-top:3px; }

  /* Client grid */
  .cm-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(320px,1fr)); gap:16px; }

  .cm-card { background:var(--panel); border:1px solid var(--border); border-radius:18px; overflow:hidden; transition:all 0.22s; animation:fadeUp 0.4s ease both; cursor:pointer; }
  .cm-card:hover { border-color:rgba(59,158,255,0.25); box-shadow:0 8px 32px rgba(59,158,255,0.08); transform:translateY(-2px); }

  .cm-card-top { padding:20px 20px 16px; display:flex; align-items:flex-start; gap:14px; }
  .cm-avatar { width:46px; height:46px; border-radius:14px; display:flex; align-items:center; justify-content:center; font-size:16px; font-weight:800; flex-shrink:0; letter-spacing:-0.02em; }
  .cm-avatar-blue   { background:linear-gradient(135deg,rgba(26,111,212,0.4),rgba(59,158,255,0.2)); color:var(--ai-blue); border:1px solid rgba(59,158,255,0.2); }
  .cm-avatar-teal   { background:linear-gradient(135deg,rgba(14,165,233,0.3),rgba(34,211,168,0.15)); color:var(--teal); border:1px solid rgba(14,165,233,0.2); }
  .cm-avatar-purple { background:linear-gradient(135deg,rgba(91,33,182,0.4),rgba(124,58,237,0.2)); color:var(--purple); border:1px solid rgba(124,58,237,0.2); }
  .cm-avatar-green  { background:linear-gradient(135deg,rgba(15,110,86,0.4),rgba(34,211,168,0.2)); color:var(--success); border:1px solid rgba(34,211,168,0.2); }

  .cm-card-meta { flex:1; min-width:0; }
  .cm-card-name { font-size:15px; font-weight:700; color:var(--text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-bottom:4px; }
  .cm-card-tin  { font-size:11px; color:var(--muted); font-family:'JetBrains Mono',monospace; }

  .cm-card-chips { display:flex; flex-wrap:wrap; gap:6px; padding:0 20px 14px; }
  .cm-chip { display:inline-flex; align-items:center; gap:5px; padding:4px 10px; border-radius:20px; font-size:11px; font-weight:500; background:rgba(255,255,255,0.04); border:1px solid var(--border); color:var(--muted-light); }
  .cm-chip-dot { width:5px; height:5px; border-radius:50%; background:currentColor; flex-shrink:0; opacity:0.6; }

  .cm-card-divider { height:1px; background:var(--border); margin:0 20px; }

  .cm-card-body { padding:14px 20px; }
  .cm-field-row { display:flex; align-items:center; gap:8px; margin-bottom:8px; font-size:12px; }
  .cm-field-row:last-child { margin-bottom:0; }
  .cm-field-icon { font-size:13px; width:18px; text-align:center; flex-shrink:0; opacity:0.6; }
  .cm-field-label { color:var(--muted); min-width:52px; flex-shrink:0; }
  .cm-field-val { color:var(--muted-light); flex:1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .cm-field-val.highlight { color:var(--ai-blue); }

  .cm-card-footer { display:flex; gap:8px; padding:14px 20px; border-top:1px solid var(--border); }
  .cm-btn-view { flex:1; padding:9px 0; background:rgba(59,158,255,0.1); border:1px solid rgba(59,158,255,0.2); border-radius:10px; color:var(--ai-bright); font-family:'Outfit',sans-serif; font-size:13px; font-weight:600; cursor:pointer; transition:all 0.2s; }
  .cm-btn-view:hover { background:rgba(59,158,255,0.18); border-color:rgba(59,158,255,0.35); }
  .cm-btn-del { padding:9px 14px; background:rgba(239,68,68,0.07); border:1px solid rgba(239,68,68,0.15); border-radius:10px; color:rgba(239,68,68,0.7); font-size:14px; cursor:pointer; transition:all 0.2s; }
  .cm-btn-del:hover { background:rgba(239,68,68,0.14); color:var(--error); border-color:rgba(239,68,68,0.3); }

  /* Empty state */
  .cm-empty { text-align:center; padding:64px 24px; background:var(--panel); border:1px dashed rgba(59,158,255,0.15); border-radius:20px; }
  .cm-empty-icon { font-size:48px; opacity:0.2; margin-bottom:16px; }
  .cm-empty-title { font-size:16px; font-weight:700; color:var(--text); margin-bottom:8px; }
  .cm-empty-sub { font-size:13px; color:var(--muted); max-width:300px; margin:0 auto 20px; line-height:1.6; }

  /* Add button */
  .cm-add-btn { display:inline-flex; align-items:center; gap:8px; padding:10px 18px; background:linear-gradient(135deg,var(--ai-deep),var(--ai-blue)); border:none; border-radius:11px; color:#fff; font-family:'Outfit',sans-serif; font-size:13.5px; font-weight:700; cursor:pointer; transition:all 0.22s; box-shadow:0 4px 20px rgba(59,158,255,0.25); }
  .cm-add-btn:hover { box-shadow:0 8px 28px rgba(59,158,255,0.4); transform:translateY(-2px); }

  /* Modal */
  .cm-modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.7); z-index:200; display:flex; align-items:center; justify-content:center; padding:20px; backdrop-filter:blur(4px); animation:fadeIn 0.2s ease; }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  .cm-modal { background:var(--panel); border:1px solid rgba(59,158,255,0.15); border-radius:20px; width:100%; max-width:480px; overflow:hidden; animation:slideUp 0.25s ease; box-shadow:0 24px 80px rgba(0,0,0,0.6); }
  @keyframes slideUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }

  .cm-modal-header { display:flex; align-items:center; justify-content:space-between; padding:20px 24px 18px; border-bottom:1px solid var(--border); }
  .cm-modal-title { font-size:16px; font-weight:800; letter-spacing:-0.01em; }
  .cm-modal-close { background:rgba(255,255,255,0.06); border:1px solid var(--border); border-radius:8px; color:var(--muted); font-size:14px; width:30px; height:30px; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all 0.2s; }
  .cm-modal-close:hover { background:rgba(239,68,68,0.15); border-color:rgba(239,68,68,0.3); color:var(--error); }

  .cm-modal-body { padding:20px 24px 24px; }
  .cm-modal-row { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  .cm-modal-divider { font-size:10px; font-weight:600; color:var(--muted); letter-spacing:0.1em; text-transform:uppercase; margin:16px 0 12px; padding-bottom:8px; border-bottom:1px solid var(--border); }
  .cm-modal-actions { display:flex; gap:10px; margin-top:20px; }
  .cm-modal-submit { flex:1; padding:12px; background:linear-gradient(135deg,var(--ai-deep),var(--ai-blue)); border:none; border-radius:11px; color:#fff; font-family:'Outfit',sans-serif; font-size:14px; font-weight:700; cursor:pointer; transition:all 0.2s; }
  .cm-modal-submit:hover { box-shadow:0 4px 20px rgba(59,158,255,0.35); }
  .cm-modal-cancel { padding:12px 20px; background:rgba(255,255,255,0.05); border:1px solid var(--border); border-radius:11px; color:var(--muted-light); font-family:'Outfit',sans-serif; font-size:14px; cursor:pointer; transition:all 0.2s; }
  .cm-modal-cancel:hover { background:rgba(255,255,255,0.09); color:var(--text); }

  /* Alert banners */
  .cm-alert { padding:10px 14px; border-radius:10px; font-size:12.5px; margin-bottom:16px; display:flex; align-items:center; gap:8px; }
  .cm-alert-err { background:rgba(255,107,138,0.08); border:1px solid rgba(255,107,138,0.2); color:var(--error); }
  .cm-alert-ok  { background:rgba(34,211,168,0.08);  border:1px solid rgba(34,211,168,0.2);  color:var(--success); }

  .cm-loading { text-align:center; padding:40px; color:var(--muted); font-size:13px; }

  @media(max-width:600px){
    .cm-grid { grid-template-columns:1fr; }
    .cm-modal-row { grid-template-columns:1fr; }
    .cm-stats { flex-direction:column; }
  }
`;

const AVATAR_COLORS = ["cm-avatar-blue","cm-avatar-teal","cm-avatar-purple","cm-avatar-green"];

function getInitials(name = "") {
  return name.split(/\s+/).filter(Boolean).map(w => w[0]?.toUpperCase() || "").slice(0,2).join("") || "?";
}

function InfoRow({ icon, label, value, highlight }) {
  if (!value) return null;
  return (
    <div className="cm-field-row">
      <span className="cm-field-icon">{icon}</span>
      <span className="cm-field-label">{label}</span>
      <span className={`cm-field-val${highlight?" highlight":""}`}>{value}</span>
    </div>
  );
}

export default function ClientsManager({ onSelectClient }) {
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showModal, setModal]   = useState(false);
  const [error, setError]       = useState("");
  const [confirmModal, setConfirmModal] = useState(null);
  const [success, setSuccess]   = useState("");
  const [form, setForm]         = useState({
    name:"", tin:"", vat_number:"", contact_name:"", contact_email:"", industry:"",
  });

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try { const d = await getMyClients(); setClients(d || []); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  const set = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setError(""); setSuccess("");
    if (!form.name) { setError("Business name is required"); return; }
    try {
      await createMyClient(form);
      setSuccess("Client added successfully");
      setForm({ name:"", tin:"", vat_number:"", contact_name:"", contact_email:"", industry:"" });
      setModal(false);
      await load();
    } catch (e) { setError(e.message); }
  }

  async function del(clientId, e) {
    e.stopPropagation();
    const clientName = clients.find(x => x.id === clientId)?.name || "this client";
    setConfirmModal({
      title: "Delete Client?",
      message: `Permanently delete "${clientName}" and all linked report data?`,
      onConfirm: async () => {
        try {
          await deleteMyClient(clientId);
          setClients(p => p.filter(x => x.id !== clientId));
          setSuccess("Client deleted.");
        } catch (err) {
          setError(err.message);
        }
      },
    });
  }

  const activeCount = clients.length;

  return (
    <>
      <style>{CM_CSS}</style>
      <div className="cm-page">

        {/* Hero row */}
        <div className="cm-hero">
          <div className="cm-hero-row">
            <div>
              <div className="cm-title">Client Portfolio</div>
              <div className="cm-sub">Manage your clients and access their tax documents</div>
            </div>
            <button className="cm-add-btn" onClick={() => { setModal(true); setError(""); setSuccess(""); }}>
              + Add Client
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="cm-stats">
          <div className="cm-stat">
            <div className="cm-stat-icon">👥</div>
            <div className="cm-stat-body">
              <div className="cm-stat-num" style={{color:"var(--ai-blue)"}}>{activeCount}</div>
              <div className="cm-stat-label">Total Clients</div>
            </div>
          </div>
          <div className="cm-stat">
            <div className="cm-stat-icon">📊</div>
            <div className="cm-stat-body">
              <div className="cm-stat-num" style={{color:"var(--success)"}}>
                {clients.filter(c => c.vat_number).length}
              </div>
              <div className="cm-stat-label">VAT Registered</div>
            </div>
          </div>
          <div className="cm-stat">
            <div className="cm-stat-icon">💼</div>
            <div className="cm-stat-body">
              <div className="cm-stat-num" style={{color:"var(--warning)"}}>
                {clients.filter(c => c.industry).length}
              </div>
              <div className="cm-stat-label">With Industry</div>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error   && <div className="cm-alert cm-alert-err">✗ {error}</div>}
        {success && <div className="cm-alert cm-alert-ok">✓ {success}</div>}

        {/* Content */}
        {loading ? (
          <div className="cm-loading">
            <div style={{fontSize:28,marginBottom:10,opacity:0.3}}>⏳</div>
            Loading clients…
          </div>
        ) : clients.length === 0 ? (
          <div className="cm-empty">
            <div className="cm-empty-icon">👥</div>
            <div className="cm-empty-title">No clients yet</div>
            <div className="cm-empty-sub">
              Add your first client to start managing their VAT returns in one place.
            </div>
            <button className="cm-add-btn" onClick={() => setModal(true)}>
              + Add Your First Client
            </button>
          </div>
        ) : (
          <div className="cm-grid">
            {clients.map((c, idx) => {
              const avatarClass = AVATAR_COLORS[idx % AVATAR_COLORS.length];
              const initials = getInitials(c.name);
              return (
                <div key={c.id} className="cm-card" onClick={() => onSelectClient(c.id)}>
                  <div className="cm-card-top">
                    <div className={`cm-avatar ${avatarClass}`}>{initials}</div>
                    <div className="cm-card-meta">
                      <div className="cm-card-name">{c.name}</div>
                      <div className="cm-card-tin">TIN: {c.tin || "—"}</div>
                    </div>
                  </div>

                  {/* Chips row */}
                  <div className="cm-card-chips">
                    {c.vat_number && (
                      <span className="cm-chip">
                        <span className="cm-chip-dot" style={{color:"var(--success)"}}/>
                        VAT: {c.vat_number}
                      </span>
                    )}
                    {c.industry && (
                      <span className="cm-chip">
                        <span className="cm-chip-dot"/>
                        {c.industry}
                      </span>
                    )}
                  </div>

                  <div className="cm-card-divider"/>

                  <div className="cm-card-body">
                    <InfoRow icon="👤" label="Contact" value={c.contact_name} />
                    <InfoRow icon="✉" label="Email"   value={c.contact_email} highlight />
                  </div>

                  <div className="cm-card-footer">
                    <button className="cm-btn-view" onClick={e => { e.stopPropagation(); onSelectClient(c.id); }}>
                      Open Profile →
                    </button>
                    <button className="cm-btn-del" title="Delete client" onClick={e => del(c.id, e)}>
                      🗑
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add client modal */}
        {showModal && (
          <div className="cm-modal-overlay" onClick={() => setModal(false)}>
            <div className="cm-modal" onClick={e => e.stopPropagation()}>
              <div className="cm-modal-header">
                <span className="cm-modal-title">Add New Client</span>
                <button className="cm-modal-close" onClick={() => setModal(false)}>✕</button>
              </div>
              <div className="cm-modal-body">
                {error && <div className="cm-alert cm-alert-err">✗ {error}</div>}

                <div className="cm-modal-divider">Business Details *</div>
                <div className="cm-modal-row">
                  <div className="field">
                    <label>Business Name *</label>
                    <input name="name" value={form.name} onChange={set} placeholder="e.g. ABC Trading Ltd" />
                  </div>
                  <div className="field">
                    <label>TIN <span style={{fontSize:10,color:"var(--muted)",fontWeight:400}}>(optional)</span></label>
                    <input name="tin" value={form.tin} onChange={set} placeholder="e.g. 123456789" />
                  </div>
                </div>
                <div className="cm-modal-row">
                  <div className="field">
                    <label>VAT Number <span style={{fontSize:10,color:"var(--muted)",fontWeight:400}}>(optional)</span></label>
                    <input name="vat_number" value={form.vat_number} onChange={set} placeholder="e.g. SZ123456" />
                  </div>
                  <div className="field">
                    <label>Industry</label>
                    <input name="industry" value={form.industry} onChange={set} placeholder="e.g. Retail" />
                  </div>
                </div>

                <div className="cm-modal-divider">Contact Information</div>
                <div className="field">
                  <label>Contact Name</label>
                  <input name="contact_name" value={form.contact_name} onChange={set} placeholder="Primary contact person" />
                </div>
                <div className="field">
                  <label>Contact Email</label>
                  <input type="email" name="contact_email" value={form.contact_email} onChange={set} placeholder="contact@business.com" />
                </div>

                <div className="cm-modal-actions">
                  <button className="cm-modal-submit" onClick={submit}>Add Client</button>
                  <button className="cm-modal-cancel" onClick={() => setModal(false)}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    
      <ConfirmModal state={confirmModal} onClose={() => setConfirmModal(null)} />
    </>
  );
}